import { ModuleDocContent } from "./docsData";

export const assignmentModuleDocs: Record<string, ModuleDocContent> = {
  "certification-assignment": {
    title: "Certification Assignment",
    subtitle: "Assign & Track Certifications",
    color: "violet",
    badge: "Certification Assignment",
    description:
      "Assign certifications to individuals, departments, roles, or via bulk CSV import. Configure deadlines, notifications, and reminders. Preview assignments with duplicate detection and track recent assignments.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Certification Assignment</strong> lets administrators assign published certifications to learners at scale. Uses a two-column layout with certification selection on the left and recipient selection on the right, with 4 assignment modes.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c6753283-1405-4363-b906-714951bb0f53.png" alt="Certification Assignment" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certification assignment interface</p></div>

      <section>
        <h2>Assignment Tabs</h2>
        <p>Four tabs control how recipients are selected:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Individual</strong></td>
              <td>Search and select specific users by name, email, or employee ID. Filter by department and role. Shows each user's assigned/completed certification counts.</td>
            </tr>
            <tr>
              <td><strong>Department</strong></td>
              <td>Search and select departments by name. Shows group key, description, and Root Group/Sub-group badge.</td>
            </tr>
            <tr>
              <td><strong>Role-Based</strong></td>
              <td>Select a department first, then choose roles within it. Selected roles appear as dismissible chip tags.</td>
            </tr>
            <tr>
              <td><strong>Bulk Import</strong></td>
              <td>Upload a CSV file with columns: type, identifier, certification_slug, deadline_days, is_mandatory, message, send_email_notification, enable_automatic_reminder, reminder_frequency_days.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Select Certifications (Left Panel)</h2>
        <p>Published certifications only (green badge). Search by title. Each item shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Thumbnail</strong></td><td>Certification image or amber gradient fallback.</td></tr>
            <tr><td><strong>Title &amp; Description</strong></td><td>Certification name and summary.</td></tr>
            <tr><td><strong>Time Limit</strong></td><td>Exam time limit in minutes.</td></tr>
            <tr><td><strong>Questions &amp; Pass Score</strong></td><td>Question count and required pass percentage.</td></tr>
          </tbody>
        </table>
        <p>Paginated with 10, 20, or 50 items per page.</p>
      </section>

      <section>
        <h2>Assignment Settings (Preview Modal)</h2>
        <p>After selecting certifications and recipients, click <strong>Preview Assignment</strong> to open a modal with duplicate detection and these settings:</p>
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Mark as mandatory</strong></td><td>Checkbox &mdash; required completion.</td></tr>
            <tr><td><strong>Set Deadline</strong></td><td>Number of days with computed due date preview.</td></tr>
            <tr><td><strong>Send email notification</strong></td><td>Checkbox (default: on).</td></tr>
            <tr><td><strong>Enable automatic reminders</strong></td><td>Checkbox with frequency input ("Every N days", default: 7).</td></tr>
            <tr><td><strong>Message</strong></td><td>Optional message to recipients.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Recent Assignments Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Assigned Users</strong></td><td>First user/group/role with "+N more" expandable.</td></tr>
            <tr><td><strong>Certifications</strong></td><td>Certification names with "+N more" or external link.</td></tr>
            <tr><td><strong>Skipped Users</strong></td><td>Users skipped due to existing assignments.</td></tr>
            <tr><td><strong>Mandatory</strong></td><td>True/False badge.</td></tr>
            <tr><td><strong>Assigned By</strong></td><td>Assigner name and email.</td></tr>
            <tr><td><strong>Deadline Days</strong></td><td>Number of days or N/A.</td></tr>
            <tr><td><strong>Status</strong></td><td>ACTIVE, COMPLETED, or CANCELLED badge.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Reminder button to send email reminders.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Mandatory compliance certification</div>
            <div class="sz-use-case-flow">
              Select the compliance certification, switch to Department tab, select all relevant departments. Preview to check for duplicates. Set mandatory, 30-day deadline, and enable reminders every 7 days. Assign.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Bulk import for large-scale rollout</div>
            <div class="sz-use-case-flow">
              Prepare a CSV with user identifiers, certification slugs, and settings. Switch to the Bulk Import tab and upload. The system processes all assignments at once with individual error reporting.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>The Preview step automatically detects duplicate assignments and shows which users will be skipped, preventing redundant notifications.</p>
      </div>

    </div>`,
  },

  "learning-path-assignment": {
    title: "Learning Path Assignment",
    subtitle: "Assign & Track Learning Paths",
    color: "indigo",
    badge: "Learning Path Assignment",
    description:
      "Assign learning paths to individuals, departments, roles, or via bulk CSV import. Same assignment workflow as Certification Assignment with green theme and learning path-specific metadata.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Learning Path Assignment</strong> enables administrators to assign multi-course learning journeys at scale. Uses the same two-column layout and 4-tab workflow as Certification Assignment, themed in green.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/cb3e5957-02e0-444b-b1a0-9edd1d64c010.png" alt="Learning Path Assignment" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learning Path Assignment — select paths and recipients</p></div>

      <section>
        <h2>Assignment Tabs</h2>
        <p>Four tabs identical to Certification Assignment:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Individual</strong></td>
              <td>Search users by name, email, or employee ID. Filter by department and role. Shows each user's assigned/completed learning path counts.</td>
            </tr>
            <tr>
              <td><strong>Department</strong></td>
              <td>Search and select departments.</td>
            </tr>
            <tr>
              <td><strong>Role-Based</strong></td>
              <td>Select department then roles. Selected roles shown as chip tags.</td>
            </tr>
            <tr>
              <td><strong>Bulk Import</strong></td>
              <td>CSV upload with columns: type, identifier, learning_path_slug, deadline_days, is_mandatory, message, send_email_notification, enable_automatic_reminder, reminder_frequency_days.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Select Learning Paths (Left Panel)</h2>
        <p>Published paths only (green badge). Search by title. Each item shows thumbnail, title, description, and course count.</p>
      </section>

      <section>
        <h2>Assignment Settings</h2>
        <p>Same settings as Certification Assignment: mandatory checkbox, deadline (stored as computed date), email notification, automatic reminders with frequency, and optional message.</p>
      </section>

      <section>
        <h2>Recent Assignments Table</h2>
        <p>Same structure as Certification Assignment: Assigned Users, Learning Paths, Skipped Users, Mandatory, Assigned By, Deadline Days, Status, and Reminder action button.</p>
      </section>

      <section>
        <h2>Key Differences from Certification Assignment</h2>
        <table>
          <thead>
            <tr>
              <th>Aspect</th>
              <th>Certification</th>
              <th>Learning Path</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Color theme</strong></td><td>Amber / orange</td><td>Green / emerald</td></tr>
            <tr><td><strong>Item metadata</strong></td><td>Time limit, questions, pass score</td><td>Course count</td></tr>
            <tr><td><strong>Deadline storage</strong></td><td>Days (integer)</td><td>Computed date</td></tr>
            <tr><td><strong>Bulk CSV field</strong></td><td>certification_slug</td><td>learning_path_slug</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">New hire onboarding</div>
            <div class="sz-use-case-flow">
              Select the onboarding learning path. Switch to Department tab and select the new hire's department. Set mandatory with a 90-day deadline. Enable weekly reminders. Assign.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Role-based skill development</div>
            <div class="sz-use-case-flow">
              Select a technical skill path. Switch to Role-Based tab, pick the Engineering department, then select Junior Developer and Mid-Level Developer roles. Preview and assign with appropriate deadlines.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Use the Reminder button in the Recent Assignments table to send follow-up emails to learners who haven't started their assigned paths.</p>
      </div>

    </div>`,
  },
};
